<template>
  <div>
    <breadcrumbs :breadcrumb-items="breadcrumbItems" />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CreativeToolbar
        :banner="banner"
        closing
      />
    </b-card>

    <CreativeNavigation v-if="authUserData.role.includes('admin')" />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4 style="margin: 0;">
              <a
                href="#"
                @click.prevent="goToSources"
              >DSP Sources </a> &gt; {{ currentPartnerName }}
            </h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="loading"
              @input="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-tabs
        v-else
        v-model="activeTab"
        class="mb-2"
      >
        <b-tab :title="$t('rtbTabs.byDate')">
          <div
            v-if="!statsByDate.length"
            class="text-center text-muted my-3"
          >
            {{ $t('No data for this period') }}
          </div>
          <b-table
            v-else
            :items="statsByDate"
            :fields="dateColumns"
            responsive
            striped
            foot-clone
            small
          >
            <template #head(date)>
              {{ $t('stats.date') }}
            </template>
            <template #head(impressions)>
              {{ $t('stats.impressions') }}
            </template>
            <template #head(clicks)>
              {{ $t('stats.clicks') }}
            </template>
            <template #head(ctr)>
              CTR
            </template>
            <template #head(revenue)>
              {{ $t('stats.revenue') }}
            </template>
            <template #head(spent)>
              {{ $t('stats.spent') }}
            </template>
            <template #cell(date)="data">
              {{ data.value ? getFormattedDate(new Date(data.value)) : '' }}
            </template>
            <template #cell(impressions)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(clicks)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(ctr)="data">
              {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
            </template>
            <template #cell(revenue)="data">
              {{ data.value ? currency('USD', data.value) : 0 }}
            </template>
            <template #cell(spent)="data">
              {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
            </template>
            <template #foot(date)="data">
              <strong :data-label="data.label">{{ $t('Records') }}: {{ statsByDate.length }}</strong>
            </template>
            <template #foot(impressions)="data">
              <strong :data-label="data.label">{{ totalFormat(statsByDate, 'impressions').toLocaleString('ru-RU') }}</strong>
            </template>
            <template #foot(clicks)="data">
              <strong :data-label="data.label">{{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(totalFormat(statsByDate, 'clicks')) }}</strong>
            </template>
            <template #foot(ctr)="data">
              <strong :data-label="data.label">
                {{ totalFormat(statsByDate, 'clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(totalFormat(statsByDate, 'clicks') / totalFormat(statsByDate, 'impressions') * 100) : 0 }}%
              </strong>
            </template>
            <template #foot(revenue)="data">
              <strong :data-label="data.label">{{ currency('USD', totalAmount(statsByDate)) }}</strong>
            </template>
            <template #foot(spent)="data">
              <strong :data-label="data.label">{{ currency(campaign.currency || 'USD', totalFormat(statsByDate, 'spent')) }}</strong>
            </template>
          </b-table>
        </b-tab>
        <b-tab :title="$t('rtbTabs.bySource')">
          <div
            v-if="!statsBySite.length"
            class="text-center text-muted my-3"
          >
            {{ $t('No data for this period') }}
          </div>
          <b-table
            v-else
            :items="statsBySite"
            :fields="siteColumns"
            responsive
            striped
            foot-clone
            small
            :sort-by.sync="siteSortBy"
            :sort-desc.sync="siteSortDesc"
            :sort-direction="siteSortDirection"
            @sort-changed="onSiteSortChanged"
          >
            <template #head(site_id)>
              {{ $t('stats.source') }}
            </template>
            <template #head(impressions)>
              {{ $t('stats.impressions') }}
            </template>
            <template #head(clicks)>
              {{ $t('stats.clicks') }}
            </template>
            <template #head(ctr)>
              CTR
            </template>
            <template #head(revenue)>
              {{ $t('stats.revenue') }}
            </template>
            <template #head(spent)>
              {{ $t('stats.spent') }}
            </template>
            <template #cell(site_id)="data">
              <div class="d-flex">
                <b-button
                  variant="link"
                  class="p-0 text-decoration-none"
                  @click="goToSourceDetails(data.value)"
                >
                  {{ getDomainNameById(data.value) || data.value }}
                </b-button>
                <feather-icon
                  v-if="getDomainNameById(data.value)"
                  icon="ExternalLinkIcon"
                  size="14"
                  class="ml-25 cursor-pointer text-primary"
                  @click.stop="openWebsite(getDomainNameById(data.value))"
                />
              </div>
            </template>
            <template #cell(impressions)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(clicks)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(ctr)="data">
              {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
            </template>
            <template #cell(revenue)="data">
              {{ data.value ? currency('USD', data.value) : 0 }}
            </template>
            <template #cell(spent)="data">
              {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
            </template>
            <template #foot(site_id)="data">
              <strong :data-label="data.label">{{ $t('Records') }}: {{ siteTotalRecords }}</strong>
            </template>
            <template #foot(impressions)="data">
              <strong :data-label="data.label">{{ totalFormat(statsBySite, 'impressions').toLocaleString('ru-RU') }}</strong>
            </template>
            <template #foot(clicks)="data">
              <strong :data-label="data.label">{{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(totalFormat(statsBySite, 'clicks')) }}</strong>
            </template>
            <template #foot(ctr)="data">
              <strong :data-label="data.label">
                {{ totalFormat(statsBySite, 'clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(totalFormat(statsBySite, 'clicks') / totalFormat(statsBySite, 'impressions') * 100) : 0 }}%
              </strong>
            </template>
            <template #foot(revenue)="data">
              <strong :data-label="data.label">{{ currency('USD', totalAmount(statsBySite)) }}</strong>
            </template>
            <template #foot(spent)="data">
              <strong :data-label="data.label">{{ currency(campaign.currency || 'USD', totalFormat(statsBySite, 'spent')) }}</strong>
            </template>
          </b-table>

          <!-- Pagination for statsBySite -->
          <div
            v-if="statsBySite.length"
            class="d-flex justify-content-between align-items-center mt-2"
          >
            <div class="d-flex align-items-center">
              <span class="mr-2">{{ $t('message.pagelength') }}:</span>
              <b-form-select
                v-model="siteLimit"
                :options="siteLimitOptions"
                size="sm"
                class="w-auto"
                @change="onSiteLimitChange"
              />
              <span class="ml-2"> {{ $t('message.of') }} {{ siteTotalRecords }} {{ $t('message.pageText2') }}</span>
            </div>
            <b-pagination
              v-model="siteCurrentPage"
              :total-rows="siteTotalRecords"
              :per-page="siteLimit"
              :limit="5"
              align="right"
              size="md"
              @change="onSitePageChange"
            />
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import {
  BCard, BTable, BTabs, BTab, BSpinner, BButton, BPagination, BFormSelect,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CreativeToolbar from '@/views/dashboard/creatives/CreativeToolbar.vue'
import CreativeNavigation from '@/views/dashboard/creatives/CreativeNavigation.vue'
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import StatsDateRangeFilter from '@/views/dashboard/dsppartners/stats/StatsDateRangeFilter.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'RtbStatsPartner',
  components: {
    Breadcrumbs,
    UserToolbar,
    CreativeToolbar,
    CreativeNavigation,
    BCard,
    BTable,
    BTabs,
    BTab,
    BSpinner,
    BButton,
    BPagination,
    BFormSelect,
    FeatherIcon,
    StatsDateRangeFilter,
  },
  data() {
    return {
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      breadcrumbItems: [],
      account: {},
      campaign: {},
      partnerNames: {},
      banner: {},
      rangePicker: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
      activeTab: 0,
      statsByDate: [],
      statsBySite: [],
      dateColumns: [
        { key: 'date', label: 'Дата', tdClass: 'td-width' },
        { key: 'impressions', label: 'Impressions', tdClass: 'td-width' },
        { key: 'clicks', label: 'Clicks', tdClass: 'td-width' },
        { key: 'ctr', label: 'CTR', tdClass: 'td-width' },
        { key: 'revenue', label: 'Revenue', tdClass: 'td-width' },
        { key: 'spent', label: 'Spent', tdClass: 'td-width' },
      ],
      siteColumns: [
        {
          key: 'site_id',
          label: 'Site',
          tdClass: 'td-width',
          sortable: false,
        },
        {
          key: 'impressions',
          label: 'Impressions',
          tdClass: 'td-width',
          sortable: true,
        },
        {
          key: 'clicks',
          label: 'Clicks',
          tdClass: 'td-width',
          sortable: true,
        },
        {
          key: 'ctr',
          label: 'CTR',
          tdClass: 'td-width',
          sortable: false,
        },
        {
          key: 'revenue',
          label: 'Revenue',
          tdClass: 'td-width',
          sortable: true,
        },
        {
          key: 'spent',
          label: 'Spent',
          tdClass: 'td-width',
          sortable: false,
        },
      ],
      loading: true,
      // Pagination for statsBySite
      siteLimit: 20,
      siteCurrentPage: 1,
      siteTotalRecords: 0,
      siteLimitOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      // Sorting for statsBySite
      siteSortBy: 'impressions',
      siteSortDesc: true,
      siteSortDirection: 'desc',
    }
  },
  computed: {
    ...mapGetters('dashboard-campaings-stats', ['campaignNameByIdFromStore']),
    getDomainNameById() {
      return siteId => {
        const domainName = this.$store.getters['dashboard-dsp-domains/domainNameByIdFromStore'](siteId)

        return domainName
      }
    },
    siteOffset() {
      return (this.siteCurrentPage - 1) * this.siteLimit
    },
    currentPartnerName() {
      return this.getPartnerName(this.$route.params.partnerId) || this.$route.params.partnerId
    },
  },
  created() {
    this.getUser()
    this.getCampaignData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
    }
  },
  methods: {
    async getUser() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      await this.getBannerData()
      await this.getStatsData()
    },
    async getStatsData() {
      this.loading = true
      try {
        await Promise.all([
          this.getStatsByDate(),
          this.getStatsBySite(),
          this.loadPartnerNames(),
        ])
        // Update breadcrumbs after partner names are loaded
        this.setBreabcrumbs()
      } catch (error) {
        this.statsByDate = []
        this.statsBySite = []
      } finally {
        this.loading = false
      }
    },
    async getStatsByDate() {
      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.statsByDate = []
        return
      }

      const params = {
        partner_id: this.$route.params.partnerId,
        campaign_id: this.$route.params.id,
        ad_id: this.$route.params.bannerId,
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
      }
      const responseData = await useJwt.getPhoenixDspPartner(params)
      this.statsByDate = responseData.data.items || []
    },
    async getStatsBySite() {
      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.statsBySite = []
        this.siteTotalRecords = 0
        return
      }

      const params = {
        campaign_id: this.$route.params.id,
        ad_id: this.$route.params.bannerId,
        partner_id: this.$route.params.partnerId,
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
        limit: this.siteLimit,
        offset: this.siteOffset,
        sort_by: this.siteSortBy,
        sort_desc: this.siteSortDesc,
      }
      const responseData = await useJwt.getPhoenixDspSiteTotal(params)
      this.statsBySite = responseData.data.items || []
      this.siteTotalRecords = responseData.data.total || this.statsBySite.length

      // Load domain names for sites
      const siteIds = [...new Set(this.statsBySite.map(item => item.site_id || item.site).filter(Boolean))]

      if (siteIds.length) {
        try {
          await this.$store.dispatch('dashboard-dsp-domains/fetchDomainNamesById', {
            dspId: this.$route.params.partnerId,
            siteIds,
          })
        } catch (error) {
          // Error loading domain names
        }
      }
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        this.siteCurrentPage = 1 // Reset to first page when changing date range
        // Update URL without page reload
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.getStatsData()
        this.saveSettings()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    refreshData() {
      this.getStatsData()
    },
    saveSettings() {
      try {
        localStorage.setItem('rtbStatsPartnerDateFilter', JSON.stringify({ rangePicker: this.rangePicker }))
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to save date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('rtbStatsPartnerDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async loadPartnerNames() {
      try {
        const responseData = await useJwt.dsppartners()
        const partners = responseData.data.dsppartners || []
        this.partnerNames = {}
        partners.forEach(partner => {
          this.partnerNames[partner._id] = partner.name
        })
      } catch (error) {
        // Error loading partner names
      }
    },
    getPartnerName(partnerId) {
      return this.partnerNames[partnerId] || null
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('Users'),
          to: '/users/',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}/`,
        },
        {
          text: this.$t('Campaigns'),
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}`,
        },
        {
          text: this.$t('Creatives'),
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/cratives`,
        },
        {
          text: this.banner.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/creatives/${this.$route.params.bannerId}`,
        },
        {
          text: 'RTB Sources',
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/creatives/${this.$route.params.bannerId}/rtb-stats/sources/`,
        },
        {
          text: this.currentPartnerName,
          active: true,
        },
      ]
    },
    totalFormat(arr, field) {
      let total = 0
      arr.forEach(i => {
        if (field === 'site') return // Skip site field for totals
        total += i[field] || 0
      })
      return total
    },
    totalAmount(arr) {
      let total = 0
      arr.forEach(i => {
        total += i.revenue || 0
      })
      return total
    },
    goToSources() {
      this.$router.push({ name: 'creatives-rtb-stats-sources', params: { userId: this.$route.params.userId, id: this.$route.params.id } })
    },
    goToSourceDetails(siteId) {
      // Navigate to source details page
      this.$router.push({
        name: 'creatives-rtb-stats-source',
        params: {
          userId: this.$route.params.userId,
          id: this.$route.params.id,
          partnerId: this.$route.params.partnerId,
          sourceId: siteId,
        },
      })
    },
    openWebsite(domainName) {
      if (!domainName) return

      // Add protocol if missing
      let url = domainName
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`
      }

      // Open in new window
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    onSitePageChange(page) {
      this.siteCurrentPage = page
      this.getStatsBySite()
    },
    onSiteLimitChange() {
      this.siteCurrentPage = 1 // Reset to first page when changing limit
      this.getStatsBySite()
    },
    onSiteSortChanged(ctx) {
      // Update sorting only for fields supported by backend
      const backendSortableFields = ['impressions', 'clicks', 'revenue']
      if (backendSortableFields.includes(ctx.sortBy)) {
        this.siteSortBy = ctx.sortBy
        this.siteSortDesc = ctx.sortDesc
        this.siteCurrentPage = 1 // Reset to first page when changing sort
        this.getStatsBySite()
      }
    },
    async getBannerData() {
      const responseData = await useJwt.getBanner(this.$route.params.bannerId)
      this.banner = responseData.data.banner || {}
    },
  },
}
</script>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

h4 {
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: #776bf0;
    }
  }
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}

.ml-25 {
  margin-left: 0.25rem;
}

.cursor-pointer {
  cursor: pointer;
}

.text-primary {
  color: #007bff !important;
}
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
[dir] .table-responsive .table-responsive {
  margin-bottom: 0!important;
}

[dir] .table.b-table > tbody td.td-width {
  width: 14%;
}
</style>
